import ActivityMonthGroup from "@Components/game/activity/activity-month-group";
import { SessionsFooter } from "@Components/game/activity/styled-components";
import { useActivityContext } from "@Context/game/activity/activity-context";
import useI18n from "@i18n";
import { CircularProgress, Stack } from "@mui/material";
import { useEffect, useLayoutEffect, useRef } from "react";

const ActivitySessions = () => {
  const { i18n } = useI18n();
  const { expandedMonths, expandNewMonths, groupedSessions, pagination } = useActivityContext();
  const sessionsRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const canLoadMore = pagination.status !== "exhausted";
  const isLoadingMore = pagination.status === "loading-more";

  useLayoutEffect(() => {
    const sessionsElement = sessionsRef.current;

    if (!sessionsElement || groupedSessions.length === 0) {
      return;
    }

    const viewportElement = sessionsElement.closest("#ACTIVITY");
    const viewportBottom = viewportElement?.getBoundingClientRect().bottom ?? window.innerHeight;
    const availableHeight = Math.max(0, viewportBottom - sessionsElement.getBoundingClientRect().top);
    const monthElements = sessionsElement.querySelectorAll<HTMLElement>("[data-activity-month]");
    const visibleRowsHeight = groupedSessions.reduce((height, group, index) => {
      if (expandedMonths[group.month] === false) {
        return height;
      }

      const rowsElement = monthElements[index]?.querySelector<HTMLElement>("[data-activity-session-rows]");

      return height + (rowsElement?.scrollHeight ?? 0);
    }, 0);

    expandNewMonths(groupedSessions.map(group => group.month));

    if (visibleRowsHeight < availableHeight && pagination.status === "ready") {
      pagination.loadNextPage();
    }
  }, [expandNewMonths, expandedMonths, groupedSessions, pagination.loadNextPage, pagination.status]);

  useEffect(() => {
    const scrollContainer = loadMoreRef.current?.closest<HTMLElement>("#ACTIVITY");

    if (!canLoadMore || !scrollContainer) {
      return;
    }

    let previousScrollTop = scrollContainer.scrollTop;

    const handleScroll = () => {
      const currentScrollTop = scrollContainer.scrollTop;
      const isScrollingDown = currentScrollTop > previousScrollTop;
      const distanceFromBottom = scrollContainer.scrollHeight - currentScrollTop - scrollContainer.clientHeight;

      previousScrollTop = currentScrollTop;

      if (isScrollingDown && distanceFromBottom <= 200) {
        pagination.loadNextPage();
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [canLoadMore, pagination.loadNextPage]);

  return (
    <Stack ref={sessionsRef} gap={4}>
      {groupedSessions.map(group => (
        <ActivityMonthGroup key={group.month} group={group} />
      ))}

      {canLoadMore && (
        <SessionsFooter ref={loadMoreRef} minHeight={4}>
          <CircularProgress
            size={32}
            aria-label={i18n("activity.load-more")}
            aria-hidden={!isLoadingMore}
            sx={{ color: "dsPrimary.v300", visibility: isLoadingMore ? "visible" : "hidden" }}
          />
        </SessionsFooter>
      )}
    </Stack>
  );
};

export default ActivitySessions;
